import { useState } from 'react';

import {
  Alert,
  Box,
  Button as MuiButton,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import { say, type Voice } from '@/james-playground/aa-family-claim/claim/asks';
import {
  text,
  type Facts,
} from '@/james-playground/aa-family-claim/claim/facts';
import {
  BANK_CARD,
  FIELD,
  QUESTION,
} from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  Field,
  Toggle,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  digitsOnly,
  formatSortCode,
  isAccountNumber,
  isSortCode,
  known,
  str,
} from '@/james-playground/aa-family-claim/claim/cards/values';

type Account = {
  holder: string;
  bank: string;
  sortCode: string;
  account: string;
  hasRoll: string;
  roll: string;
};

const problems = (a: Account) => ({
  holder: !a.holder.trim(),
  bank: !a.bank.trim(),
  sortCode: !isSortCode(a.sortCode),
  account: !isAccountNumber(a.account),
  hasRoll: a.hasRoll !== 'Yes' && a.hasRoll !== 'No',
  roll: a.hasRoll === 'Yes' && !a.roll.trim(),
});
const complete = (a: Account) => !Object.values(problems(a)).some(Boolean);

const fromFacts = (facts: Facts): Account => ({
  holder:
    str(known(facts, 'bankAccountHolder')) ||
    [text(facts, 'firstNames'), text(facts, 'surname')]
      .filter(Boolean)
      .join(' '),
  bank: str(known(facts, 'bankName')),
  sortCode: str(known(facts, 'bankSortCode')),
  account: str(known(facts, 'bankAccountNumber')),
  hasRoll: str(known(facts, 'bankAccountHasRollNumber')),
  roll: str(known(facts, 'bankRollNumber')),
});

/**
 * The account details, typed in a sheet of their own. Bank details never go
 * in the conversation, where they would sit in the transcript, so the reply
 * says only that they were added, and the card shows the last digits.
 */
const AccountSheet = ({
  start,
  v,
  onSave,
  onClose,
}: {
  start: Account;
  v: Voice;
  onSave: (a: Account) => void;
  onClose: () => void;
}) => {
  const [a, setA] = useState(start);
  const [tried, setTried] = useState(false);
  const wrong = problems(a);
  const set = (key: keyof Account) => (value: string) =>
    setA((x) => ({ ...x, [key]: value }));
  const shown = (key: keyof Account) => tried && wrong[key];

  return (
    <BottomSheet
      variant='full'
      title={BANK_CARD.sheetTitle}
      onClose={onClose}
      footer={(close) => (
        <MuiButton
          variant='contained'
          size='large'
          fullWidth
          onClick={() => {
            if (!complete(a)) return setTried(true);
            onSave({
              ...a,
              holder: a.holder.trim(),
              bank: a.bank.trim(),
              sortCode: formatSortCode(a.sortCode),
              account: digitsOnly(a.account),
              roll: a.hasRoll === 'Yes' ? a.roll.trim() : '',
            });
            close();
          }}
        >
          {BANK_CARD.save}
        </MuiButton>
      )}
    >
      <Stack spacing={2.5} sx={{ pb: 2 }}>
        <Typography variant='body1'>{say(BANK_CARD.body, v)}</Typography>
        <Alert severity='info'>{BANK_CARD.demo}</Alert>
        <Field
          size='medium'
          label={BANK_CARD.holder}
          value={a.holder}
          onChange={(e) => set('holder')(e.target.value)}
          error={shown('holder')}
          helperText={shown('holder') ? BANK_CARD.invalidHolder : undefined}
          autoComplete='off'
        />
        <Field
          size='medium'
          label={BANK_CARD.bank}
          value={a.bank}
          onChange={(e) => set('bank')(e.target.value)}
          error={shown('bank')}
          helperText={shown('bank') ? BANK_CARD.invalidBank : undefined}
          autoComplete='off'
        />
        <Field
          size='medium'
          label={BANK_CARD.sortCode}
          value={a.sortCode}
          onChange={(e) => set('sortCode')(formatSortCode(e.target.value))}
          error={shown('sortCode')}
          helperText={
            shown('sortCode')
              ? BANK_CARD.invalidSortCode
              : BANK_CARD.sortCodeHint
          }
          autoComplete='off'
          slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 8 } }}
        />
        <Field
          size='medium'
          label={BANK_CARD.account}
          type='password'
          value={a.account}
          onChange={(e) =>
            set('account')(digitsOnly(e.target.value).slice(0, 10))
          }
          error={shown('account')}
          helperText={
            shown('account') ? BANK_CARD.invalidAccount : BANK_CARD.accountHint
          }
          autoComplete='off'
          slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 10 } }}
        />
        <Toggle
          question={say(QUESTION.bankAccountHasRollNumber, v)}
          hint={BANK_CARD.rollHint}
          value={a.hasRoll}
          onChange={set('hasRoll')}
        />
        {shown('hasRoll') && (
          <Typography variant='body2' sx={{ color: 'error.main' }}>
            {BANK_CARD.invalidRoll}
          </Typography>
        )}
        {a.hasRoll === 'Yes' && (
          <Field
            size='medium'
            label={FIELD.bankRollNumber as string}
            value={a.roll}
            onChange={(e) => set('roll')(e.target.value.slice(0, 18))}
            error={shown('roll')}
            helperText={shown('roll') ? BANK_CARD.invalidRoll : undefined}
            autoComplete='off'
          />
        )}
      </Stack>
    </BottomSheet>
  );
};

const BankCard = ({ ask, facts, voice: v, onSubmit, onSkip }: CardProps) => {
  const start = fromFacts(facts);
  const [saved, setSaved] = useState<Account | null>(
    complete(start) ? start : null,
  );
  const [open, setOpen] = useState(false);
  const [others, setOthers] = useState(
    str(known(facts, 'otherBenefitsToAccount')) === 'Yes',
  );

  const submit = () => {
    if (!saved) return;
    onSubmit(
      {
        bankAccountHolder: saved.holder,
        bankName: saved.bank,
        bankSortCode: saved.sortCode,
        bankAccountNumber: saved.account,
        bankAccountHasRollNumber: saved.hasRoll,
        ...(saved.hasRoll === 'Yes' ? { bankRollNumber: saved.roll } : {}),
        otherBenefitsToAccount: others ? 'Yes' : 'No',
      },
      BANK_CARD.reply,
    );
  };

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={Boolean(saved)}
      onContinue={submit}
      onSkip={onSkip}
    >
      <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={1.25}>
        <Box
          sx={{ display: 'flex', pt: 0.25, color: 'primary.main' }}
          aria-hidden
        >
          <Icon icon='shield-check' fontSize={16} />
        </Box>
        <Typography variant='body2'>{BANK_CARD.secure}</Typography>
      </Stack>
      {saved ? (
        <Stack spacing={1.5}>
          <Stack spacing={0.25} aria-label={BANK_CARD.saved} role='group'>
            <Typography variant='h6'>{saved.holder}</Typography>
            <Typography variant='body2'>{saved.bank}</Typography>
            <Typography variant='body2'>
              {BANK_CARD.sortEnding(digitsOnly(saved.sortCode).slice(-2))}
            </Typography>
            <Typography variant='body2'>
              {BANK_CARD.accountEnding(saved.account.slice(-4))}
            </Typography>
            {saved.hasRoll === 'Yes' && (
              <Typography variant='body2'>{BANK_CARD.roll}</Typography>
            )}
          </Stack>
          <MuiButton
            variant='outlined'
            onClick={() => setOpen(true)}
            sx={{ alignSelf: 'flex-start' }}
          >
            {BANK_CARD.change}
          </MuiButton>
          <FormControlLabel
            control={
              <Checkbox
                checked={others}
                onChange={(e) => setOthers(e.target.checked)}
              />
            }
            label={say(BANK_CARD.others, v)}
          />
        </Stack>
      ) : (
        <MuiButton
          variant='outlined'
          fullWidth
          startIcon={<Icon icon='shield-check' fontSize={14} />}
          onClick={() => setOpen(true)}
        >
          {BANK_CARD.add}
        </MuiButton>
      )}
      {open && (
        <AccountSheet
          start={saved ?? start}
          v={v}
          onSave={(a) => setSaved(a)}
          onClose={() => setOpen(false)}
        />
      )}
    </CardShell>
  );
};

BankCard.displayName = 'BankCard';
export default BankCard;
